import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { NgxMaskModule } from 'ngx-mask';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { OrderModule } from 'ngx-order-pipe';

import { AppConfig } from './utils/app-config.service';
import { CoreModule } from '../core/core.module';
import { MainRoutingModule } from './main-routing.module';
import { Loader } from './client/helpers/loader';
import { LocalStorage } from './client/helpers/local-storage';
import { MyHttpClient } from './client/api/my-http-client';
import { ParentEvents } from './utils/parent-events.service';
import { Serializer } from './client/helpers/serializer/serializer';
import { UserAuthenticated } from './client/helpers/user-authenticated';

import { ApiClient } from './client/api/api-client.service';
import { ApiClientInterceptor } from './client/api/api-client.interceptor';
import {
    AgentFactoryInterface,
    ApiClientInterface,
    OperationFactoryInterface,
    SessionClientInterface,
    TransactionRequestInterface,
} from '../core';
import { ClientApiClient } from './client/api/client-api-client.service';
import { ContractApiClient } from './client/api/contract-api-client.service';
import { DocumentApiClient } from './client/api/document-api-client.service';
import { LegalApiClient } from './client/api/legal-api-client.service';
import { OrderApiClient } from './client/api/order-api-client.service';
import { ProductApiClient } from './client/api/product-api-client.service';
import { SessionClient } from './client/api/session-client.service';
import { TaxApiClient } from './client/api/tax-api-client.service';
import { TestApiClient } from './client/api/test-api-client.service';
import { TransferApiClient } from './client/api/transfer-api-client.service';
import { UserApiClient } from './client/api/user-api-client.service';

import { AnalyticsService } from './gtm/analytics.service';
import { GoogleTagManagerService } from './gtm/google-tag-manager.service';

import { CreateRefundOperationUseCase } from './usecases/create-refund-operation.use-case';
import { GetClientLocalStorageUseCase } from './usecases/get-client-local-storage.use-case';

import { AssignmentOperatePage } from './pages/assignment/operate/assignment-operate.page';
import { AssignmentPage } from './pages/assignment/assignment.page';
import { AssignmentSignPage } from './pages/assignment/sign/assignment-sign.page';
import { ContractPage } from './pages/contract/contract.page';
import { ContractAdvicePage } from './pages/contract/advice/contract-advice.page';
import { ContractFormPage } from './pages/contract/form/contract-form.page';
import { ContractLandingPage } from './pages/contract/landing/contract-landing.page';
import { ContractMinorDisabledPage } from './pages/contract/minor-disabled/contract-minor-disabled.page';
import { ContractNaturalPage } from './pages/contract/natural/contract-natural.page';
import { ContractNaturePage } from './pages/contract/nature/contract-nature.page';
import { ContractParticipantsPage } from './pages/contract/participants/contract-participants.page';
import { ContractSignPage } from './pages/contract/sign/contract-sign.page';
import { ContributionOperatePage } from './pages/contribution/operate/contribution-operate.page';
import { ContributionPage } from './pages/contribution/contribution.page';
import { ContributionSignPage } from './pages/contribution/sign/contribution-sign.page';
import { DisinvestmentOperatePage } from './pages/disinvestment/operate/disinvestment-operate.page';
import { DisinvestmentPage } from './pages/disinvestment/disinvestment.page';
import { DisinvestmentSignPage } from './pages/disinvestment/sign/disinvestment-sign.page';
import { FullDataPage } from './pages/full-data/full-data.page';
import { FullDataAccountPage } from './pages/full-data/account/full-data-account.page';
import { FullDataListPage } from './pages/full-data/list/full-data-list.page';
import { FullDataProfilePage } from './pages/full-data/profile/full-data-profile.page';
import { NotFoundPage } from './pages/error/not-found/not-found.page';
import { MeetingSignPage } from './pages/operative/meeting-sign/meeting-sign.page';
import { OperativeListPage } from './pages/operative/list/operative-list.page';
import { OperativeOperatePage } from './pages/operative/operate/operative-operate.page';
import { OperativePage } from './pages/operative/operative.page';
import { OperativeProductDetailPage } from './pages/operative/product/detail/operative-product-detail.page';
import { OperativeProductListPage } from './pages/operative/product/list/operative-product-list.page';
import { OperativeSignPage } from './pages/operative/sign/operative-sign.page';
import { RepeatTestPage } from './pages/repeat-test/repeat-test.page';
import { RepeatTestQuestionPage } from './pages/repeat-test/question/repeat-test-question.page';
import { RepeatTestResultPage } from './pages/repeat-test/result/repeat-test-result.page';
import { RepeatTestResultSuitabilityPage } from './pages/repeat-test/result-suitability/repeat-test-result-suitability.page';
import { RepeatTestSignPage } from './pages/repeat-test/sign/repeat-test-sign.page';
import { RepeatTestSignHandwritingPage } from './pages/repeat-test/sign-handwriting/repeat-test-sign-handwriting.page';
import { TestPage } from './pages/test/test.page';
import { TestQuestionPage } from './pages/test/question/test-question.page';
import { TestRatePage } from './pages/test/rate/test-rate.page';
import { TestResultPage } from './pages/test/result/test-result.page';
import { TestResultSuitabilityPage } from './pages/test/result-suitability/test-result-suitability.page';
import { TestTransferPage } from './pages/test/transfer/test-transfer.page';
import { UnauthorizedPage } from './pages/error/unauthorized/unauthorized.page';
import { ViewAccountPage } from './pages/view/account/view-account.page';
import { ViewPage } from './pages/view/view.page';
import { ViewProfilePage } from './pages/view/profile/view-profile.page';

import { AutoNumericDirective } from './directives/auto-numeric.directive';
import { CIFValidatorDirective } from './validators/cif/cif.directive';
import { ClientExistsValidatorDirective } from './validators/client-exists/client-exists.directive';
import { DNIValidatorDirective } from './validators/dni/dni.directive';
import { EmailExistsValidatorDirective } from './validators/email-exists/email-exists.directive';
import { EmailValidatorDirective } from './validators/email/email.directive';
import { DateTimeFutureDirective } from './validators/date-time-future/date-time-future.directive';
import { EqualToValidatorDirective } from './validators/equal-to/equal-to.directive';
import { ExistsInContractValidatorDirective } from './validators/exists-in-contract/client-exists-in-contract.directive';
import { FilesizeValidatorDirective } from './validators/filesize/filesize.directive';
import { FiletypeValidatorDirective } from './validators/filetype/filetype.directive';
import { IbanValidatorDirective } from './validators/iban/iban.directive';
import { IsClientNaturalValidatorDirective } from './validators/is-client-natural/client-natural-exists.directive';
import { IsinValidatorDirective } from './validators/isin/isin.directive';
import { LEIValidatorDirective } from './validators/lei/lei.directive';
import { MaxValidatorDirective } from './validators/max/max.directive';
import { MinValidatorDirective } from './validators/min/min.directive';
import { NIEValidatorDirective } from './validators/nie/nie.directive';
import { NIFValidatorDirective } from './validators/nif/nif.directive';
import { OfLegalAgeDirective } from './validators/of-legal-age/of-legal-age.directive';
import { OfMinorAgeDirective } from './validators/of-minor-age/of-minor-age.directive';
import { ParentFormDirective } from './directives/parent-form.directive';
import { PhoneExistsValidatorDirective } from './validators/phone-exists/phone-exists.directive';
import { RecaptchaDirective } from './directives/recaptcha.directive';
import { UncheckedValidatorDirective } from './validators/unchecked/unchecked.directive';
import { NoWhitespacesValidatorDirective } from './validators/no-whitespaces/no-whitespaces.directive';

import { CertificateComponent } from './ui/test/certificate/certificate.component';
import { ConfirmDialog } from './ui/dialog/confirm/confirm.dialog';
import { ConfirmTransferDialog } from './ui/dialog/confirm-transfer/confirm-transfer.dialog';
import { ConfirmSignatureDialog } from './ui/dialog/confirm-signature/confirm-signature.dialog';
import { ConfirmOrderDialog } from './ui/dialog/confirm-order/confirm-order.dialog';
import { ConfirmContractDialog } from './ui/dialog/confirm-contract/confirm-contract.dialog';
import { ConfirmContractResultDialog } from './ui/dialog/confirm-contract-result/confirm-contract-result.dialog';
import { ContractFormLegalPersonComponent } from './ui/contract/form-legal-person/contract-form-legal-person.component';
import { ContractFormMinorDisabledPersonComponent } from './ui/contract/form-minor-disabled-person/contract-form-minor-disabled-person.component';
import { ContractFormNaturalPersonComponent } from './ui/contract/form-natural-person/contract-form-natural-person.component';
import { ContractFormParticipantComponent } from './ui/contract/form-participant/contract-form-participant.component';
import { ContractFormParticipantLegalPersonComponent } from './ui/contract/form-participant-legal-person/contract-form-participant-legal-person.component';
import { ContractFormParticipantMinorDisabledPersonComponent } from './ui/contract/form-participant-minor-disabled-person/contract-form-participant-minor-disabled-person.component';
import { ContractFormParticipantNaturalPersonComponent } from './ui/contract/form-participant-natural-person/contract-form-participant-natural-person.component';
import { ContractProgressAdviceComponent } from './ui/contract/progress-advice/contract-progress-advice.component';
import { ContractProgressAssignmentComponent } from './ui/contract/progress-assignment/contract-progress-assignment.component';
import { ContractProgressComponent } from './ui/contract/progress/contract-progress.component';
import { ContractProgressContributionComponent } from './ui/contract/progress-contribution/contract-progress-contribution.component';
import { ContractProgressDisinvestmentComponent } from './ui/contract/progress-disinvestment/contract-progress-disinvestment.component';
import { ContractProgressGDCComponent } from './ui/contract/progress-gdc/contract-progress-gdc.component';
import { DictionaryDialog } from './ui/dialog/dictionary/dictionary.dialog';
import { DoughnutChartComponent } from './ui/charts/doughnut/doughnut-chart.component';
import { EditableComponent } from './ui/editable/editable.component';
import { ErrorDialog } from './ui/dialog/error/error.dialog';
import { ErrorAddingParticipantDialog } from './ui/dialog/error-adding-participant/error-adding-participant.dialog';
import { FileSelectorComponent } from './ui/file-selector/file-selector.component';
import { FullDataAppropriatenessTableComponent } from './ui/full-data/appropriateness-table/full-data-appropriateness-table.component';
import { FullDataEightNaturalPersonComponent } from './ui/full-data/eight-natural-person/full-data-eight-natural-person.component';
import { FullDataFiveNaturalPersonComponent } from './ui/full-data/five-natural-person/full-data-five-natural-person.component';
import { FullDataFiveLegalPersonComponent } from './ui/full-data/five-legal-person/full-data-five-legal-person.component';
import { FullDataFormAddressComponent } from './ui/full-data/form-address/full-data-form-address.component';
import { FullDataFormCompanyComponent } from './ui/full-data/form-company/full-data-form-company.component';
import { FullDataFormDocumentsComponent } from './ui/full-data/form-documents/full-data-form-documents.component';
import { FullDataFormFatcaSingleManagerComponent } from './ui/full-data/form-fatca-single-manager/full-data-form-fatca-single-manager.component';
import { FullDataFormManagerComponent } from './ui/full-data/form-manager/full-data-form-manager.component';
import { FullDataFormRelationshipComponent } from './ui/full-data/form-relationship/full-data-form-relationship.component';
import { FullDataFormShareholderAddressComponent } from './ui/full-data/form-shareholder-address/full-data-form-shareholder-address.component';
import { FullDataFormShareholderComponent } from './ui/full-data/form-shareholder/full-data-form-shareholder.component';
import { FullDataFormShareholderLegalComponent } from './ui/full-data/form-shareholder-legal/full-data-form-shareholder-legal.component';
import { FullDataFormTaxComponent } from './ui/full-data/form-tax/full-data-form-tax.component';
import { FullDataFormLegalTaxComponent } from './ui/full-data/form-legal-tax/full-data-form-legal-tax.component';
import { FullDataFourLegalPersonComponent } from './ui/full-data/four-legal-person/full-data-four-legal-person.component';
import { FullDataFourNaturalPersonComponent } from './ui/full-data/four-natural-person/full-data-four-natural-person.component';
import { FullDataIbanTableComponent } from './ui/full-data/iban-table/full-data-iban-table.component';
import { FullDataInputDocumentComponent } from './ui/full-data/input-document/full-data-input-document.component';
import { FullDataLegalTableComponent } from './ui/full-data/legal-table/full-data-legal-table.component';
import { FullDataNineNaturalPersonComponent } from './ui/full-data/nine-natural-person/full-data-nine-natural-person.component';
import { FullDataOneLegalPersonComponent } from './ui/full-data/one-legal-person/full-data-one-legal-person.component';
import { FullDataOneMinorDisabledPersonComponent } from './ui/full-data/one-minor-disabled-person/full-data-one-minor-disabled-person.component';
import { FullDataOneNaturalPersonComponent } from './ui/full-data/one-natural-person/full-data-one-natural-person.component';
import { FullDataParticipantsTableComponent } from './ui/full-data/participants-table/full-data-participants-table.component';
import { FullDataPupilsTableComponent } from './ui/full-data/pupils-table/full-data-pupils-table.component';
import { FullDataSevenAdminPbcComponent } from './ui/full-data/seven-admin-pbc/full-data-seven-admin-pbc.component';
import { FullDataShareholderTableComponent } from './ui/full-data/shareholder-table/full-data-shareholder-table.component';
import { FullDataSixLegalPersonComponent } from './ui/full-data/six-legal-person/full-data-six-legal-person.component';
import { FullDataThreeLegalPersonComponent } from './ui/full-data/three-legal-person/full-data-three-legal-person.component';
import { FullDataThreeMinorDisabledPersonComponent } from './ui/full-data/three-minor-disabled-person/full-data-three-minor-disabled-person.component';
import { FullDataThreeNaturalPersonComponent } from './ui/full-data/three-natural-person/full-data-three-natural-person.component';
import { FullDataTwoLegalPersonComponent } from './ui/full-data/two-legal-person/full-data-two-legal-person.component';
import { FullDataTwoMinorDisabledPersonComponent } from './ui/full-data/two-minor-disabled-person/full-data-two-minor-disabled-person.component';
import { FullDataTwoNaturalPersonComponent } from './ui/full-data/two-natural-person/full-data-two-natural-person.component';
import { LegalDialog } from './ui/dialog/legal/legal.dialog';
import { LoadingComponent } from './ui/loading/loading.component';
import { HeaderOnboardingComponent } from './ui/header/onboarding/header-onboarding.component';
import { HeaderOnboardingClientPortalComponent } from './ui/header/onboarding-client-portal/header-onboarding-client-portal.component';
import { HeaderOperativeComponent } from './ui/header/operative/header-operative.component';
import { OperateExternalTransferComponent } from './ui/operate/external-transfer/operate-external-transfer.component';
import { OperateFormSignComponent } from './ui/operate/form-sign/operate-form-sign.component';
import { OperateInternalTransferComponent } from './ui/operate/internal-transfer/operate-internal-transfer.component';
import { OperateRefundComponent } from './ui/operate/refund/operate-refund.component';
import { OperateRefundInternalTransferComponent } from './ui/operate/refund-internal-transfer/operate-refund-internal-transfer.component';
import { OperateRefundPeriodComponent } from './ui/operate/refund-period/operate-refund-period.component';
import { OperateSubscriptionComponent } from './ui/operate/subscription/operate-subscription.component';
import { OperateSubscriptionPeriodComponent } from './ui/operate/subscription-period/operate-subscription-period.component';
import { OrderCardComponent } from './ui/product/order-card/order-card.component';
import { PhoneDialog } from './ui/dialog/phone/phone.dialog';
import { ProductCardComponent } from './ui/product/product-card/product-card.component';
import { ProductRefundCardComponent } from './ui/product/product-refund-card/product-refund-card.component';
import { RateFormComponent } from './ui/rate/rate-form.component';
import { SelectedProductCardComponent } from './ui/product/selected-product-card/selected-product-card.component';
import { TermsAndConditionsDialog } from './ui/dialog/terms-and-conditions/terms-and-conditions.dialog';
import { TestStartComponent } from './ui/test/start/test-start.component';
import { TestStartSuitabilityComponent } from './ui/test/start-suitability/test-start-suitability.component';
import { TestStepCircleComponent } from './ui/test/step-circle/test-step-circle.component';
import { TestStepGroupListComponent } from './ui/test/step-group-list/test-step-group-list.component';
import { TestStepListComponent } from './ui/test/step-list/test-step-list.component';
import { ThrRadioButton, ThrRadioGroup } from './ui/radio-button/radio-button.component';
import { TransferFormComponent } from './ui/transfer/form/transfer-form.component';
import { TransferFormFundComponent } from './ui/transfer/form-fund/transfer-form-fund.component';
import { ValidationNotificationsDialog } from './ui/dialog/validation-notifications/validation-notifications.dialog';
import { ValidationReasonDialog } from './ui/dialog/validation-reason/validation-reason.dialog';
import { ViewAccountIbanTableComponent } from './ui/view/account/iban-table/view-account-iban-table.component';
import { ViewProfileLegalTableComponent } from './ui/view/profile/legal-table/view-profile-legal-table.component';
import { ViewProfileParticipantsTableComponent } from './ui/view/profile/participants-table/view-profile-participants-table.component';

import { AgentFactory } from './domain/factories/agent-factory';
import { OperationFactory } from './domain/factories/operation-factory';
import { TransactionRequest } from './domain/transaction/transaction-request';

import { ChangedResolver } from './resolvers/changed.resolver';
import { DisinvestmentResolver } from './resolvers/disinvestment.resolver';
import { FeeResolver } from './resolvers/fee.resolver';
import { TransferResolver } from './resolvers/transfer.resolver';

import { environment } from '../../environments/environment';

import '@angular/common/locales/global/es';
import 'moment/locale/es';

const APP_PAGES = [
    AssignmentOperatePage,
    AssignmentPage,
    AssignmentSignPage,
    ContractPage,
    ContractAdvicePage,
    ContractFormPage,
    ContractLandingPage,
    ContractMinorDisabledPage,
    ContractNaturePage,
    ContractNaturalPage,
    ContractParticipantsPage,
    ContractSignPage,
    ContributionOperatePage,
    ContributionPage,
    ContributionSignPage,
    DisinvestmentOperatePage,
    DisinvestmentPage,
    DisinvestmentSignPage,
    FullDataPage,
    FullDataAccountPage,
    FullDataListPage,
    FullDataProfilePage,
    TestTransferPage,
    NotFoundPage,
    MeetingSignPage,
    OperativeListPage,
    OperativeOperatePage,
    OperativePage,
    OperativeProductDetailPage,
    OperativeProductListPage,
    OperativeSignPage,
    RepeatTestPage,
    RepeatTestQuestionPage,
    RepeatTestResultPage,
    RepeatTestResultSuitabilityPage,
    RepeatTestSignPage,
    RepeatTestSignHandwritingPage,
    TestPage,
    TestQuestionPage,
    TestRatePage,
    TestResultPage,
    TestResultSuitabilityPage,
    UnauthorizedPage,
    ViewAccountPage,
    ViewPage,
    ViewProfilePage,
];

const APP_DIRECTIVES = [
    AutoNumericDirective,
    CIFValidatorDirective,
    ClientExistsValidatorDirective,
    DNIValidatorDirective,
    EmailExistsValidatorDirective,
    DateTimeFutureDirective,
    EmailValidatorDirective,
    EqualToValidatorDirective,
    ExistsInContractValidatorDirective,
    FilesizeValidatorDirective,
    FiletypeValidatorDirective,
    IbanValidatorDirective,
    IsClientNaturalValidatorDirective,
    IsinValidatorDirective,
    LEIValidatorDirective,
    MaxValidatorDirective,
    MinValidatorDirective,
    NIEValidatorDirective,
    NIFValidatorDirective,
    OfLegalAgeDirective,
    OfMinorAgeDirective,
    ParentFormDirective,
    PhoneExistsValidatorDirective,
    RecaptchaDirective,
    UncheckedValidatorDirective,
    NoWhitespacesValidatorDirective
];

const APP_UI = [
    CertificateComponent,
    ConfirmDialog,
    ConfirmContractDialog,
    ConfirmContractResultDialog,
    ConfirmTransferDialog,
    ConfirmSignatureDialog,
    ConfirmOrderDialog,
    ContractFormLegalPersonComponent,
    ContractFormMinorDisabledPersonComponent,
    ContractFormNaturalPersonComponent,
    ContractFormParticipantComponent,
    ContractFormParticipantLegalPersonComponent,
    ContractFormParticipantMinorDisabledPersonComponent,
    ContractFormParticipantNaturalPersonComponent,
    ContractProgressAdviceComponent,
    ContractProgressAssignmentComponent,
    ContractProgressComponent,
    ContractProgressContributionComponent,
    ContractProgressDisinvestmentComponent,
    ContractProgressGDCComponent,
    DictionaryDialog,
    DoughnutChartComponent,
    EditableComponent,
    ErrorDialog,
    ErrorAddingParticipantDialog,
    FileSelectorComponent,
    FullDataAppropriatenessTableComponent,
    FullDataEightNaturalPersonComponent,
    FullDataFiveLegalPersonComponent,
    FullDataFiveNaturalPersonComponent,
    FullDataFormAddressComponent,
    FullDataFormCompanyComponent,
    FullDataFormDocumentsComponent,
    FullDataFormFatcaSingleManagerComponent,
    FullDataFormManagerComponent,
    FullDataFormRelationshipComponent,
    FullDataFormShareholderAddressComponent,
    FullDataFormShareholderComponent,
    FullDataFormShareholderLegalComponent,
    FullDataFormTaxComponent,
    FullDataFormLegalTaxComponent,
    FullDataFourLegalPersonComponent,
    FullDataFourNaturalPersonComponent,
    FullDataIbanTableComponent,
    FullDataInputDocumentComponent,
    FullDataLegalTableComponent,
    FullDataNineNaturalPersonComponent,
    FullDataOneLegalPersonComponent,
    FullDataOneMinorDisabledPersonComponent,
    FullDataOneNaturalPersonComponent,
    FullDataParticipantsTableComponent,
    FullDataPupilsTableComponent,
    FullDataSevenAdminPbcComponent,
    FullDataShareholderTableComponent,
    FullDataSixLegalPersonComponent,
    FullDataThreeLegalPersonComponent,
    FullDataThreeMinorDisabledPersonComponent,
    FullDataThreeNaturalPersonComponent,
    FullDataTwoLegalPersonComponent,
    FullDataTwoMinorDisabledPersonComponent,
    FullDataTwoNaturalPersonComponent,
    LegalDialog,
    LoadingComponent,
    HeaderOnboardingComponent,
    HeaderOnboardingClientPortalComponent,
    HeaderOperativeComponent,
    OperateExternalTransferComponent,
    OperateFormSignComponent,
    OperateInternalTransferComponent,
    OperateRefundComponent,
    OperateRefundInternalTransferComponent,
    OperateRefundPeriodComponent,
    OperateSubscriptionComponent,
    OperateSubscriptionPeriodComponent,
    OrderCardComponent,
    PhoneDialog,
    ProductCardComponent,
    ProductRefundCardComponent,
    RateFormComponent,
    SelectedProductCardComponent,
    TermsAndConditionsDialog,
    TestStartComponent,
    TestStartSuitabilityComponent,
    TestStepCircleComponent,
    TestStepGroupListComponent,
    TestStepListComponent,
    ThrRadioGroup,
    ThrRadioButton,
    TransferFormComponent,
    TransferFormFundComponent,
    ValidationNotificationsDialog,
    ValidationReasonDialog,
    ViewAccountIbanTableComponent,
    ViewProfileLegalTableComponent,
    ViewProfileParticipantsTableComponent,
];

export function initializeApp(appConfig: AppConfig) {
    return () => appConfig.load();
}

export const MY_DATE_FORMATS = {
    parse: {
        dateInput: 'DD-MM-YYYY',
    },
    display: {
        dateInput: 'DD-MM-YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

const APP_PROVIDERS = [
    { provide: LOCALE_ID, useValue: 'es' },
    { provide: MAT_DATE_LOCALE, useValue: 'es' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: HTTP_INTERCEPTORS, useClass: ApiClientInterceptor, multi: true },
    { provide: AgentFactoryInterface, useClass: AgentFactory },
    { provide: OperationFactoryInterface, useClass: OperationFactory },
    { provide: TransactionRequestInterface, useClass: TransactionRequest },
    { provide: ApiClientInterface, useClass: ApiClient },
    { provide: SessionClientInterface, useClass: SessionClient },

    AppConfig,
    {
        provide: APP_INITIALIZER,
        useFactory: initializeApp,
        deps: [AppConfig],
        multi: true,
    },

    Loader,
    LocalStorage,
    MyHttpClient,
    ParentEvents,
    Serializer,
    UserAuthenticated,

    ClientApiClient,
    ContractApiClient,
    DocumentApiClient,
    LegalApiClient,
    OrderApiClient,
    TransferApiClient,
    ProductApiClient,
    TaxApiClient,
    TestApiClient,
    TransferApiClient,
    UserApiClient,

    ChangedResolver,
    DisinvestmentResolver,
    FeeResolver,
    TransferResolver,

    AnalyticsService,
    GoogleTagManagerService,

    CreateRefundOperationUseCase,
    GetClientLocalStorageUseCase,
];

const TranslateConfig = {
    loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => {
            return new TranslateHttpLoader(http, `${environment.baseHref}assets/i18n/`, `.json?v=${environment.version}`);
        },
        deps: [HttpClient],
    },
};

@NgModule({
    declarations: [...APP_PAGES, ...APP_DIRECTIVES, ...APP_UI],
    id: 'main-module',
    imports: [
        BrowserAnimationsModule,
        CommonModule,
        CoreModule,
        FormsModule,
        HttpClientModule,
        MainRoutingModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatCardModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatDialogModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMomentDateModule,
        MatProgressSpinnerModule,
        MatRadioModule,
        MatSelectModule,
        MatSliderModule,
        MatSlideToggleModule,
        MatTooltipModule,
        NgxMaskModule.forRoot(),
        ReactiveFormsModule,
        TranslateModule.forRoot(TranslateConfig),
        OrderModule
    ],
    providers: APP_PROVIDERS,
    entryComponents: [
        ConfirmDialog,
        ConfirmContractDialog,
        ConfirmContractResultDialog,
        ErrorDialog,
        ErrorAddingParticipantDialog,
        DictionaryDialog,
        LegalDialog,
        OperativeOperatePage,
        PhoneDialog,
        TermsAndConditionsDialog,
        ValidationNotificationsDialog,
        ValidationReasonDialog,
    ],
})
export class MainModule {}
